export const bulkGeneration = {
  brandVoice: {
    label: 'Brand Voice',
    loading: 'Loading brand voices...',
    placeholder: 'Select a brand voice',
    new: 'New',
    create: 'Create Brand Voice',
  },
  interlinking: {
    label: 'Interlinking Website',
    description: 'Select a website to find and insert relevant internal links in generated content',
    placeholder: 'Select a website (optional)',
    empty: 'No websites configured. Add one in organization settings.',
    manage: 'Manage Websites',
  },
  folder: {
    label: 'Destination Folder',
    description: 'All generated articles will be saved to this folder',
    placeholder: 'Select a folder (optional)',
    empty: 'No folders yet. Create one to organize your content.',
    create: 'Create Folder',
    change: 'Change folder',
    searchFolders: 'Search folders...',
    noFoldersFound: 'No folders found',
    namePlaceholder: 'Folder name...',
    alreadyExists: 'A folder with this name already exists',
    color: 'Color',
    cancel: 'Cancel',
  },
  tags: {
    label: 'Tags',
    description: 'Tags will be applied to all generated articles in this batch',
    addTag: 'Add tag',
    searchTags: 'Search tags...',
    noTagsFound: 'No tags found',
    empty: 'No tags yet',
    createNewTag: 'Create new tag',
    tagNamePlaceholder: 'Tag name...',
    alreadyExists: 'A tag with this name already exists',
    tagColor: 'Color',
    cancel: 'Cancel',
    create: 'Create',
    selected: '{{count}} tags selected',
  },
  factUpdate: {
    label: 'Fact Update',
    description: 'Enrich content with recent data and statistics from the web',
    enabled: 'Enable fact update',
    timeframe: 'Timeframe',
    timeframes: {
      last_24h: 'Last 24 hours',
      last_week: 'Last week',
      last_month: 'Last month',
      last_3_months: 'Last 3 months',
      last_year: 'Last year',
    },
  },
  imageConfig: {
    label: 'Image Generation',
    description: 'Configure image generation settings for all articles in this batch',
    count: 'Images per article',
    countHelper: '(0-5 images)',
    prompt: 'Section images style (optional)',
    promptPlaceholder: 'e.g., Minimalist, corporate colors',
    heroPrompt: 'Hero image style (optional)',
    heroPromptPlaceholder: 'e.g., Cinematic, wide panoramic, bold colors',
    heroPromptHelper: 'Specific instructions for the cover/hero image. Falls back to section style if empty',
    styleLabel: 'Visual style',
    sourceLabel: 'Image source',
    sourceStock: 'Stock Photos',
    sourceAi: 'AI Generated',
    stockHelper:
      'Images will be sourced from Magnific stock photos based on your content title and sections',
    aiFallbackLabel: 'Use AI images if stock is unavailable',
    aiFallbackHelper:
      'If the stock provider fails, AI-generated images are used instead. Turn off to leave the content without images.',
  },
  actions: {
    addManually: 'Add Manually',
    uploadCSV: 'Upload CSV',
    loadSampleData: 'Load Sample Data',
    generateAllBriefs: 'Generate All Briefs ({{count}})',
    generatingBriefs: 'Generating {{current}}/{{total}}...',
    delete: 'Delete',
  },
  status: {
    valid: '{{count}} ready',
    needAttention: '{{count}} with errors',
    selected: '{{count}} selected',
    itemsTotal: '{{count}} items',
  },
  empty: {
    title: 'Start a bulk generation',
    description: 'Upload a CSV or add rows manually to draft up to 50 articles at once.',
    uploadCSV: 'Upload CSV',
    addManually: 'Add manually',
  },
  settings: {
    title: 'Batch settings',
    description: 'These settings apply to every article generated in this batch.',
    button: 'Settings',
    clear: 'Clear',
  },
  generate: {
    selectBrandVoiceFirst: 'Select a brand voice first',
    noSelection: 'Select items to generate',
    selectionSummary: '{{selected}} of {{total}} selected',
    invalidSelected: '{{count}} selected item(s) have errors. Fix or deselect to continue.',
    initiating: 'Initiating...',
    generate: 'Generate',
    generateArticle: 'Generate {{count}} article',
    generateArticles: 'Generate {{count}} articles',
    error: 'Failed to initiate bulk generation. Please try again.',
  },
  table: {
    columns: {
      title: 'Title',
      mainKeyword: 'Main Keyword',
      secondaryKeywords: 'Sec. Keywords',
      entity: 'Entity',
      approach: 'Approach',
      language: 'Lang',
      questions: 'Questions',
      sources: 'Sources',
      customPrompt: 'Custom Prompt',
      brief: 'Brief',
      status: 'Status',
      error: 'Error',
      actions: 'Actions',
      row: 'Row',
    },
    empty: 'No items yet. Click "Add Manually" to start adding content.',
    placeholders: {
      title: 'Content title...',
      keyword: 'Keyword...',
      secondaryKeywords: 'kw1, kw2...',
      entity: 'Entity...',
      approach: 'Describe approach...',
      questions: 'One question per line...',
      sources: 'url1, url2...',
      customPrompt: 'Extra instructions...',
      brief: 'Brief (min 100 chars)...',
    },
    brief: {
      generateTooltip: 'Generate brief with AI',
      disabledTooltip: 'Fill title, keyword, entity & approach first',
      generating: 'Generating...',
      aiBrief: 'AI Brief',
    },
    validation: {
      missing: 'Missing: {{fields}}',
    },
    openEditor: 'Open full editor',
  },
  csv: {
    dropHere: 'Drop CSV file here...',
    dragDrop: 'Drag & drop CSV file here, or click to select',
    maxRows: 'Maximum 50 rows',
    validating: 'Validating...',
    validate: 'Validate & Upload',
    downloadTemplate: 'Download Template',
    clearFile: 'Clear',
    validation: {
      valid: '{{count}} valid',
      invalid: '{{count}} invalid',
      totalRows: '{{count}} total rows',
      proceed: 'Proceed with {{count}} items',
      validBadge: 'Valid',
      invalidBadge: 'Invalid',
    },
  },
  csvDialog: {
    title: 'Import CSV',
    description: 'Upload a CSV file with your content plan.',
    templateTitle: 'CSV Template',
    templateDescription: 'Download the template with all accepted fields',
    uploadAnother: 'Upload another',
    limitWarning: 'Only {{max}} of {{valid}} valid rows will be imported (50 items max)',
    cancel: 'Cancel',
    import: 'Import {{count}} items',
  },
  jobProgress: {
    title: 'Bulk Generation Progress',
    jobId: 'Job ID:',
    loading: 'Loading job details...',
    notFound: 'Job not found',
    retrying: 'Retrying...',
    retryFailed: 'Retry Failed ({{count}})',
    items: 'Items ({{count}})',
    allCompleted: 'All items completed!',
    allCompletedWithFailed: 'All items completed! ({{count}} failed)',
  },
  itemStatus: {
    pending: 'Pending',
    queued: 'Queued',
    processing: 'Generating',
    completed: 'Completed',
    failed: 'Failed',
  },
  progress: {
    completed: '{{completed}} / {{total}} completed',
    completedLabel: 'Completed:',
    generatingLabel: 'Generating:',
    failedLabel: 'Failed:',
  },
  editor: {
    untitled: 'Untitled Item',
    itemOf: 'Item {{current}} of {{total}}',
    contentData: 'Content Data',
    fields: {
      title: 'Title',
      mainKeyword: 'Main Keyword',
      mainEntity: 'Main Entity',
      secondaryKeywords: 'Secondary Keywords',
      contentApproach: 'Content Approach',
      language: 'Language',
      questionsToAnswer: 'Questions to Answer',
      referenceSources: 'Reference Sources',
      customPrompt: 'Custom Prompt',
      linkedinPosts: 'LinkedIn Posts',
      internalSources: 'Internal Sources',
    },
    placeholders: {
      title: 'Enter content title',
      mainKeyword: 'Primary keyword',
      mainEntity: 'Main entity',
      secondaryKeywords: 'keyword1, keyword2, keyword3',
      contentApproach: 'Describe the angle and structure of the content...',
      questionsToAnswer: 'One question per line',
      referenceSources: 'https://example.com, https://another.com',
      customPrompt: 'Additional instructions for the AI...',
      brief: 'Describe the content structure, key topics to cover, and specific angles to take...',
      linkedinPostUrl: 'https://linkedin.com/posts/...',
      internalSourceUrl: 'https://example.com/resource',
    },
    help: {
      separateWithCommas: 'Separate with commas',
      oneQuestionPerLine: 'One question per line',
      linkedinPostsHelp: 'Add LinkedIn post URLs as reference sources for content generation',
      internalSourcesHelp: 'Add URLs to internal documents or pages to use as source material',
    },
    linkedinPosts: {
      add: 'Add LinkedIn Post',
      urlLabel: 'Post URL',
      remove: 'Remove',
      empty: 'No LinkedIn posts added',
    },
    internalSources: {
      add: 'Add Source',
      urlLabel: 'Source URL',
      remove: 'Remove',
      empty: 'No internal sources added',
    },
    languages: {
      en: 'English',
      es: 'Espa\u00f1ol',
      pt: 'Portugu\u00eas',
    },
    brief: {
      label: 'Brief',
      generating: 'Generating...',
      generate: 'Generate Brief',
      charactersNeeded: '{{count}} more characters needed',
      meetsMinimum: 'Brief meets minimum length',
      charCount: '{{current}}/{{max}}',
    },
    disabledReasons: {
      selectBrandVoice: 'Select a brand voice first',
      titleRequired: 'Title is required',
      mainKeywordRequired: 'Main keyword is required',
      mainEntityRequired: 'Main entity is required',
      approachTooShort: 'Content approach needs at least 10 characters',
    },
    actions: {
      close: 'Close',
      saveAndClose: 'Save & Close',
    },
  },
  routes: {
    bulkGeneration: 'Bulk Generation',
    bulkJobProgress: 'Bulk Job Progress',
  },
  itemsTable: {
    view: 'View',
  },
} as const
